import React from 'react';
import { CenteredSpinner } from '@components/ui/feedback/CenteredSpinner';
import { Box, Card, CardContent, Typography } from '@mui/material';

import { ActionWrapper } from './ActionWrapper';
import type { FeatureCardProps } from './FeatureCard.types';

/**
 * FeatureCard Component
 *
 * A composable card component for displaying features with optional icon, badge, and action.
 * The action area is absolutely positioned at the bottom with a gradient overlay for a smooth transition.
 *
 * @example
 * ```tsx
 * <FeatureCard
 *   icon={<MicrophoneIcon />}
 *   title="Record an Encounter"
 *   badge={<Chip label="New" size="small" />}
 * >
 *   <AudioVisualizer />
 *   <Button variant="contained" fullWidth>
 *     Record Encounter
 *   </Button>
 * </FeatureCard>
 * ```
 */
export const FeatureCard: React.FC<FeatureCardProps> = ({
  icon,
  title,
  badge,
  children,
  action,
  footer,
  contentHeight,
  fullHeight = false,
  sx,
  contentSx,
  onClick,
  loading = false,
}) => {
  return (
    <Card
      onClick={onClick}
      sx={{
        position: 'relative',
        height: fullHeight ? '100%' : 'auto',
        minHeight: action ? 320 : 'auto',
        display: 'flex',
        flexDirection: 'column',
        cursor: onClick ? 'pointer' : 'default',
        transition: 'transform 0.15s ease-in-out',
        overflow: 'hidden',
        borderRadius: 3,
        border: '1px solid',
        borderColor: 'borderColors.primary',
        boxShadow: 0,
        '&:hover': onClick
          ? {
              transform: 'scale(1.02)',
            }
          : {},
        '&:active': onClick
          ? {
              transform: 'scale(0.98)',
            }
          : {},
        ...sx,
      }}
    >
      {/* Header Section */}
      <Box
        sx={{
          py: 1.5,
          px: 2,
          minHeight: 56,
          display: 'flex',
          alignItems: 'center',
          borderBottom: '1px solid',
          borderColor: 'borderColors.primary',
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%' }}>
          {icon && (
            <Box
              sx={{
                color: 'primary.main',
                display: 'flex',
                alignItems: 'center',
                '& svg': { fontSize: 20 },
              }}
            >
              {icon}
            </Box>
          )}
          <Box sx={{ flex: 1 }}>
            <Typography
              variant="h6"
              sx={{
                fontWeight: 500,
              }}
            >
              {title}
            </Typography>
          </Box>
          {badge && <Box sx={{ ml: 'auto' }}>{badge}</Box>}
        </Box>
      </Box>

      {/* Content Section */}
      <CardContent
        sx={{
          flex: 1,
          backgroundColor: 'backgroundColors.secondary',
          height: contentHeight,
          overflow: 'auto',
          p: 2,
          position: 'relative',
          ...contentSx,
        }}
      >
        {loading ? <CenteredSpinner /> : children}
      </CardContent>

      {/* Action Section - Absolutely positioned with gradient */}
      {action && <ActionWrapper>{action}</ActionWrapper>}

      {/* Footer Section - Positioned at bottom with border-top */}
      {footer && (
        <Box sx={{ p: 2, borderTop: '1px solid', borderColor: 'borderColors.primary' }}>
          {footer}
        </Box>
      )}
    </Card>
  );
};

export default FeatureCard;
